import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import SetMode from 'app/components/SetMode';
import { Mode } from 'core/slices/session';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { Header } from 'app/components/Header/Loadable';
import { LearnersHeader } from './Chart/Loadable';
import { Tabs } from 'app/components/Tabs';
import { useState } from 'react';
import { People } from './People/Loadable';
import { Notification } from './Notification/Loadable';
import {
  BoltIcon,
  CheckBadgeIcon,
  EnvelopeIcon,
  RectangleStackIcon,
  RssIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Admin } from './Admin/Loadable';
import { Publish } from './Publish/Loadable';
import { selectCanAdmin, selectCanShare } from 'core/selectors/admin/coursePermission';
import { CourseStatusHeader } from '../Status';
import { selectIsDefaultCourse } from 'core/selectors/admin/course';
import { CourseContent } from '../Modules/Loadable';
import { useNavigate, useParams } from 'react-router-dom';
import { EntityTypes } from 'constants/entities';
import { SECTIONS } from 'constants/sections';
import { updateState, restoreState } from 'core/actions/product/state';
import { reportRequest } from 'core/services/admin';
import { FlowPage, IFlowState, flowActions } from 'core/slices/flow';
import { ReportType } from 'core/slices/report';
import { Records } from './Records';

export enum CourseTabs {
  MODULES = 0,
  LEARNERS = 1,
  RECORDS = 2,
  NOTIFICATION = 3,
  PUBLISH = 4,
  EDITORS = 5,
}

export const CourseUsers = React.memo(({ tab = CourseTabs.MODULES }: { tab?: CourseTabs }) => {
  const [tabIndex, setTabIndex] = useState<CourseTabs>(tab);

  const canAdmin = useAppSelector(selectCanAdmin);
  const canShare = useAppSelector(selectCanShare);
  const isDefault = useAppSelector(state => selectIsDefaultCourse(state, undefined));
  const navigate = useNavigate();
  const courseId = useAppSelector(state => state.state[EntityTypes.COURSE]);
  const flowState: IFlowState = useAppSelector(state => state.flow);
  const params = useParams();
  const defaultCourseId = useAppSelector(state => state.session.courseId);
  const dispatch = useAppDispatch();

  const onChangeTab = React.useCallback(
    (index: CourseTabs) => {
      if (index !== tab && courseId) {
        switch (index) {
          case CourseTabs.MODULES:
            navigate(`/v1/${SECTIONS.COURSE}/${courseId}/modules`, { replace: false });
            break;
          case CourseTabs.LEARNERS:
            navigate(`/v1/${SECTIONS.COURSE}/${courseId}/learners`, { replace: false });
            break;
          case CourseTabs.RECORDS:
            navigate(`/v1/${SECTIONS.COURSE}/${courseId}/records`, { replace: false });
            break;
          case CourseTabs.NOTIFICATION:
            navigate(`/v1/${SECTIONS.COURSE}/${courseId}/notification`, { replace: false });
            break;
          case CourseTabs.PUBLISH:
            navigate(`/v1/${SECTIONS.COURSE}/${courseId}/publish`, { replace: false });
            break;
          case CourseTabs.EDITORS:
            navigate(`/v1/${SECTIONS.COURSE}/${courseId}/editors`, { replace: false });
            break;
        }
        setTabIndex(index);
      }
    },
    [courseId, navigate, tab],
  );

  React.useEffect(() => {
    const cId = parseInt(params.courseId) || defaultCourseId;
    if (cId && cId !== courseId) {
      dispatch(updateState({ type: EntityTypes.COURSE, id: cId }));
      // dispatch(getRequest({ type: EntityTypes.COURSE, entityId: cId }));

      dispatch(
        reportRequest({
          type: EntityTypes.COURSE,
          reportType: ReportType.COURSE,
          parentEntityId: cId,
          pagerInfo: { page: 0, ids: [cId] },
        }),
      );
    }

    if (flowState.page !== FlowPage.COURSE_CONTENT) dispatch(flowActions.updateFlowPage(FlowPage.COURSE_CONTENT));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, courseId]);

  React.useEffect(() => {
    dispatch(
      restoreState([
        { type: EntityTypes.COURSE_NODE, id: null },
        { type: EntityTypes.MODULE, id: null },
        { type: EntityTypes.ACTION, id: null },
        { type: EntityTypes.SCENE, id: null },
      ]),
    );
    dispatch(flowActions.selectModNode(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = React.useMemo(
    () => [
      {
        label: (
          <>
            <RectangleStackIcon className={`inline-flex mx-2 w-5 h-5 rotate-90`} />
            Modules
          </>
        ),
        component: <CourseContent />,
      },
      {
        label: (
          <>
            <UsersIcon className="inline-flex mx-2 w-5 h-5" />
            Learners
          </>
        ),
        component: <People />,
      },
      {
        label: (
          <>
            <CheckBadgeIcon className="inline-flex mx-2 w-5 h-5" />
            Records
          </>
        ),
        component: <Records />,
      },
      ...(isDefault
        ? []
        : [
            {
              label: (
                <>
                  <EnvelopeIcon className="inline-flex mx-2 w-5 h-5" />
                  Notification
                </>
              ),
              component: <Notification />,
            },
            ...(!canShare
              ? []
              : [
                  {
                    label: (
                      <>
                        <RssIcon className="inline-flex mx-2 w-5 h-5" />
                        Publish
                      </>
                    ),
                    component: <Publish />,
                  },
                ]),
          ]),

      ...(!canAdmin
        ? []
        : [
            {
              label: (
                <>
                  <UserGroupIcon className="inline-flex mx-2 w-5 h-5" />
                  Editors
                </>
              ),
              component: <Admin />,
            },
          ]),
    ],
    [canAdmin, canShare, isDefault],
  );

  return (
    <>
      <Helmet>
        <title>Admin</title>
        <meta name="campaign builder" content="" />
      </Helmet>
      {!isDefault && <Header />}

      <SetMode mode={Mode.ADMIN} />

      <div className="mt-20 mx-auto max-w-screen-2xl px-2 sm:px-8">
        <LearnersHeader />
        {isDefault ? null : <CourseStatusHeader className="mt-3" />}

        <Tabs
          listClassName="w-full h-full sm:mt-12 text-md howio-font-md my-8 text-gray-700 dark:text-gray-100 border-b border-slate-200 flex-wrap grid grid-cols-2 sm:flex"
          tabs={tabs}
          index={tabIndex}
          onChange={onChangeTab}
        />
      </div>
    </>
  );
});
